import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LiveCallAuthService } from '../../live-call-auth.service';
import {
  AUTH_TYPE_OPTIONS,
  CreateLiveCallAuthPayload,
  LiveCallAuthConfig,
  PatchLiveCallAuthPayload,
} from '../../live-call-auth.models';

export type AuthPanelMode = 'create' | 'edit';

/**
 * Add or edit one UNIVERSAL auth entry the live-call tools authenticate
 * against: a name, the base URL it targets, an auth scheme, and a write-only
 * token.
 *
 * The backend never returns the stored secret (responses only carry
 * `hasToken` + `tokenSuffix`), so a blank token field in edit mode means
 * "keep the current secret" — the explicit `clearToken` checkbox is the only
 * way to remove it, sending `token: null`.
 */
@Component({
  selector: 'app-auth-form-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './auth-form-panel.component.html',
})
export class AuthFormPanelComponent implements OnChanges {
  @Input() open = false;
  @Input() mode: AuthPanelMode = 'create';
  @Input() authConfig: LiveCallAuthConfig | null = null;

  @Output() saved = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  private authService = inject(LiveCallAuthService);

  saving = signal(false);
  errorMsg = signal('');

  readonly authTypeOptions = AUTH_TYPE_OPTIONS;

  /** Edit mode only — checking this sends `token: null` regardless of the token field. */
  clearToken = false;

  form: {
    name: string;
    base_url: string;
    auth_type: string;
    token: string;
    is_active: boolean;
  } = { name: '', base_url: '', auth_type: 'bearer', token: '', is_active: true };

  /** Last 4 chars of the stored secret, for the "keep current token" helper text. */
  get tokenSuffix(): string | null {
    return this.authConfig?.tokenSuffix ?? null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['authConfig'] || changes['mode'] || changes['open']) {
      this.errorMsg.set('');
      this.clearToken = false;
      if (this.mode === 'edit' && this.authConfig) {
        this.form = {
          name: this.authConfig.name,
          base_url: this.authConfig.baseUrl,
          auth_type: this.authConfig.authType,
          token: '',
          is_active: this.authConfig.isActive ?? true,
        };
      } else {
        this.form = { name: '', base_url: '', auth_type: 'bearer', token: '', is_active: true };
      }
    }
  }

  onSubmit(): void {
    this.errorMsg.set('');
    if (!this.form.name.trim()) return this.errorMsg.set('Name is required.');
    if (!this.form.base_url.trim()) return this.errorMsg.set('Base URL is required.');

    this.saving.set(true);
    const done = {
      next: () => {
        this.saving.set(false);
        this.saved.emit();
      },
      error: (err: any) => {
        this.saving.set(false);
        this.errorMsg.set(err?.message ?? 'Save failed.');
      },
    };

    if (this.mode === 'create') {
      const payload: CreateLiveCallAuthPayload = {
        name: this.form.name.trim(),
        base_url: this.form.base_url.trim(),
        auth_type: this.form.auth_type,
        is_active: this.form.is_active,
      };
      if (this.form.token.trim()) {
        payload.token = this.form.token.trim();
      }
      this.authService.createAuthConfig(payload).subscribe(done);
      return;
    }

    const payload: PatchLiveCallAuthPayload = {
      name: this.form.name.trim(),
      base_url: this.form.base_url.trim(),
      auth_type: this.form.auth_type,
      is_active: this.form.is_active,
    };
    if (this.clearToken) {
      payload.token = null;
    } else if (this.form.token.trim()) {
      payload.token = this.form.token.trim();
    }
    this.authService.patchAuthConfig(this.authConfig!.id, payload).subscribe(done);
  }

  onClose(): void {
    this.closed.emit();
  }
}
